import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/common/ScrollToTop';
import './App.css';
import { isLogin } from "./auth";
import Loader from "./components/common/Loader";

// Lazy load all pages
const HomePage = lazy(() => import('./pages/home/HomePage'));
const LoginPage = lazy(() => import('./pages/account/LoginPage'));
const ProductDetailPage = lazy(() => import('./pages/product/ProductDetailPage'));
const SubCategoryPage = lazy(() => import("./pages/subCat/subcat"));
const ProductListingPage = lazy(() => import("./pages/product/ProductListing"));
const ProductCartPage = lazy(() => import("./pages/cart/cart"));
const ProductWishlistPage = lazy(() => import("./pages/wishlist/wishlist"));
const OrdersPage = lazy(() => import("./pages/order/Order"));
const ProfilePage = lazy(() => import("./pages/profile/profile"));
const AndroidPoilcy = lazy(() => import("./pages/privacy/cressa_android_privacypolicy"));
const IOSPrivacyPolicy = lazy(() => import("./pages/privacy/cressa_ios_privacypolicy"));
const AccountDeletionSteps = lazy(() => import("./pages/privacy/cressa_account_deletion"));
const ContactUs = lazy(() => import("./pages/ContactUs/ContactUs"));
const AboutUs = lazy(() => import("./pages/about/AboutPage"));
const OrderDetailsPage = lazy(() => import("./pages/order/OrderDetail"));

// Protected Route component with loading state
const ProtectedRoute = ({ element }) => {
    return isLogin() ? <>{element}</> : <Navigate to="/account-login" />;
};

const App = () => {
    const [isPageLoading, setIsPageLoading] = useState(false);

    // Track route changes
    useEffect(() => {
        const handleRouteChangeStart = () => setIsPageLoading(true);
        const handleRouteChangeComplete = () => setIsPageLoading(false);

        // Simulate route change events since React Router doesn't expose them directly
        const originalPushState = window.history.pushState;
        window.history.pushState = function () {
            handleRouteChangeStart();
            const result = originalPushState.apply(this, arguments);
            setTimeout(() => handleRouteChangeComplete(), 300); // Give time for component to load
            return result;
        };

        return () => {
            window.history.pushState = originalPushState;
        };
    }, []);

    // Fallback loading component for Suspense
    const fallback = <Loader />;

    return (
        <Router>
            <ScrollToTop />
            <Layout>
                {isPageLoading && <Loader />}
                <Suspense fallback={fallback}>
                    <Routes>
                        {/* Public routes */}
                        <Route path="/account-login" element={isLogin() ? <Navigate to="/" /> : <LoginPage />} />
                        <Route path="/cressa_android_privacypolicy" element={<AndroidPoilcy />} />
                        <Route path="/cressa_ios_privacypolicy" element={<IOSPrivacyPolicy />} />
                        <Route path="/account-deletion" element={<AccountDeletionSteps />} />
                        <Route path="/about-us" element={<AboutUs />} />
                        <Route path="/contact" element={<ContactUs />} />

                        {/* Public routes */}
                        <Route path="/" element={<HomePage />} />
                        <Route path="/product-detail/:id" element={<ProductDetailPage />} />
                        <Route path="/sub-category/:name/:id" element={<SubCategoryPage />} />
                        <Route path="/product-listing/:name/:id" element={<ProductListingPage />} />

                        {/* Protected routes */}
                        <Route path="/product-cart" element={<ProtectedRoute element={<ProductCartPage />} />} />
                        <Route path="/product-wishlist" element={<ProtectedRoute element={<ProductWishlistPage />} />} />
                        <Route path="/my-orders" element={<ProtectedRoute element={<OrdersPage />} />} />
                        <Route path="/order-details/:id" element={<ProtectedRoute element={<OrderDetailsPage />} />} />
                        <Route path="/my-account" element={<ProtectedRoute element={<ProfilePage />} />} />

                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                </Suspense>
            </Layout>
        </Router>
    );
};

export default App;